import React, { ReactElement, ReactNode } from 'react';
import { css } from '@emotion/react';
import classNames from 'classnames';
import { IconCircleInfo, IconCircleCheck, IconProps, IconCircleWarning, IconTriangleWarning } from '../icons/generated';
import { Stack } from '../stack';
import { semantic_colors, shapes, Typography } from '../../foundation';
import { BaseText } from '../text';
import { CloseButton } from '../button';
import { banner_color_css, banner_background_color_css, banner_border_color_css } from './styles';

export type BannerStatus = 'neutral' | 'info' | 'error' | 'warning' | 'success';
export type BannerSize = 'xsmall' | 'small' | 'medium';
export type BannerDirection = 'horizontal' | 'vertical';

export interface BannerProps {
  className?: string;
  /** @default 'info' */
  status?: BannerStatus;
  /** @default 'medium' */
  size?: BannerSize;
  /** @default 'horizontal' */
  direction?: BannerDirection;
  /**
   * title 좌측에 렌더링 될 아이콘 요소
   *  - title이 없을 경우 렌더되지 않습니다.
   *  - Icon 컴포넌트 color props가 있더라도 status 따라 currentColor가 지정됩니다.
   *  - Icon 컴포넌트 size props가 있더라도 Icon 사이즈는 banner size 'medium'일경우 16, 'small'일경우 12로 고정됩니다.
   */
  leftIcon?: ReactElement<IconProps>;
  /** @default true */
  showIcon?: boolean;
  title?: string;
  message?: ReactNode;
  /**
   * 배너 우측에 렌더링 될 extra actions
   */
  actions?: ReactNode;
  /**
   * 배너 우측에 렌더링 될 CloseButton 사용 유무
   * @default false
   */
  closable?: boolean;
  onClose?: () => void;
}

const sizeStyles: Record<
  BannerSize,
  {
    wrapper: { p: string; rowGap: number; columnGap: number; actionTop: number };
    title: { kind: Typography };
    message: { kind: Typography };
    icon: { size: number; margin: string };
  }
> = {
  xsmall: {
    wrapper: {
      p: '7px 10px',
      rowGap: 2,
      columnGap: 6,
      actionTop: 6,
    },
    title: {
      kind: 'Body_12_SemiBold',
    },
    message: {
      kind: 'Body_12_Regular',
    },
    icon: {
      size: 12,
      margin: '3px 0',
    },
  },
  small: {
    wrapper: {
      p: '13px 16px',
      rowGap: 4,
      columnGap: 8,
      actionTop: 4,
    },
    title: {
      kind: 'Body_12_SemiBold',
    },
    message: {
      kind: 'Body_12_Regular',
    },
    icon: {
      size: 12,
      margin: '3px 0',
    },
  },
  medium: {
    wrapper: {
      p: '17px 20px',
      rowGap: 4,
      columnGap: 8,
      actionTop: 4,
    },
    title: {
      kind: 'Body_14_SemiBold',
    },
    message: {
      kind: 'Body_13_Regular',
    },
    icon: {
      size: 16,
      margin: '2px 0',
    },
  },
};

export const Banner = ({
  className,
  status = 'info',
  size = 'medium',
  direction = 'horizontal',
  leftIcon,
  showIcon = true,
  title,
  message,
  actions,
  closable = false,
  onClose,
}: BannerProps) => {
  const color = banner_color_css[status];
  const style = sizeStyles[size];

  const renderToIcon = () => {
    const icon = leftIcon ?? React.createElement(default_icons[status] as React.ComponentType<IconProps>);

    return (
      <span
        css={css`
          display: inline-flex;
          flex-shrink: 0;
          margin: ${style.icon.margin};
        `}
      >
        {React.cloneElement(icon, { size: style.icon.size, color })}
      </span>
    );
  };

  return (
    <Stack
      direction='column'
      className={classNames('pds-normal-banner', className)}
      css={css`
        width: 100%;
        ${shapes.border_radius.small};
        background-color: ${banner_background_color_css[status]};
        border: 1px solid ${banner_border_color_css[status]};
        color: ${color};
        word-wrap: break-word;
      `}
      p={style.wrapper.p}
    >
      <Stack gap={`0 ${style.wrapper.columnGap}px`} align='flex-start'>
        {showIcon && renderToIcon()}
        <Stack direction='column' gap={`${style.wrapper.rowGap}px 0`}>
          {title && (
            <BaseText kind={style.title.kind as Typography} color={color}>
              {title}
            </BaseText>
          )}
          {message && (
            <BaseText kind={style.message.kind as Typography} color={semantic_colors.content.primary}>
              {message}
            </BaseText>
          )}
          {direction === 'vertical' && (
            <Stack width='auto' align='flex-start' mt={style.wrapper.actionTop}>
              {actions}
            </Stack>
          )}
        </Stack>
        <Stack ml='auto' gap={`0 ${style.wrapper.columnGap}px`}>
          {direction === 'horizontal' && actions}
          {closable && (
            <CloseButton size={style.icon.size} color={semantic_colors.content.secondary} onClick={onClose} />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

const default_icons: { [key in BannerStatus]: React.ComponentType } = {
  neutral: IconCircleInfo,
  info: IconCircleInfo,
  error: IconCircleWarning,
  success: IconCircleCheck,
  warning: IconTriangleWarning,
} as const;
